import { Mask } from 'maska'

export interface PhoneCountry {
  prefix: string
  flag: string
  mask: string
  minDigits: number
  maxDigits: number
  selectable: boolean
}

export const phoneCountries: PhoneCountry[] = [
  { prefix: '+420', flag: 'i-flag-cz-4x3', mask: '### ### ###', minDigits: 9, maxDigits: 9, selectable: true },
  { prefix: '+421', flag: 'i-flag-sk-4x3', mask: '### ### ###', minDigits: 9, maxDigits: 9, selectable: true },
  { prefix: '+358', flag: 'i-flag-fi-4x3', mask: '## ### #####', minDigits: 6, maxDigits: 10, selectable: false },
]

export const selectablePhoneCountries = phoneCountries.filter(country => country.selectable)

export const defaultPhoneCountry = phoneCountries[0]!

export function digitsOnly(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '')
}

export function getPhoneCountry(prefix: string | null | undefined): PhoneCountry {
  return phoneCountries.find(country => country.prefix === prefix) ?? defaultPhoneCountry
}

export function formatPhoneDigits(digits: string, country: PhoneCountry): string {
  return new Mask({ mask: country.mask }).masked(digitsOnly(digits).slice(0, country.maxDigits))
}

export function parsePhoneNumber(value: string | null | undefined, fallbackPrefix: string = defaultPhoneCountry.prefix): { prefix: string; digits: string } {
  const raw = (value ?? '').trim()
  const fallback = getPhoneCountry(fallbackPrefix)
  
  if (!raw) return { prefix: fallback.prefix, digits: '' }

  let rest = digitsOnly(raw)
  let country = fallback
  
  const isInternational = raw.startsWith('+') || raw.startsWith('00')

  if (isInternational) {
    if (raw.startsWith('00')) rest = rest.slice(2)
    
    const match = phoneCountries.find(candidate => rest.startsWith(candidate.prefix.slice(1)))
    
    if (match) {
      country = match
      rest = rest.slice(match.prefix.length - 1)
    }
  } else {
    rest = rest.replace(/^0+/, '')
  }
  
  return { prefix: country.prefix, digits: formatPhoneDigits(rest, country) }
}

export function composePhoneNumber(prefix: string, digits: string): string {
  const country = getPhoneCountry(prefix)
  const formatted = formatPhoneDigits(digits, country)
  
  return formatted ? `${country.prefix} ${formatted}` : ''
}

export function isPhoneNumberComplete(prefix: string, digits: string): boolean {
  const country = getPhoneCountry(prefix)
  const length = digitsOnly(digits).length
  
  return length >= country.minDigits && length <= country.maxDigits
}

export function isPhoneNumberValid(value: string | null | undefined): boolean {
  const { prefix, digits } = parsePhoneNumber(value)
  
  return isPhoneNumberComplete(prefix, digits)
}
